/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { isInt, compareBigInts, normalizeString, CIVILTIME_HASH_SEED } from './internal/core';
import { getTime, getTimePart, TimePart, NANOSECONDS_PER_DAY } from './internal/time';
import { timeDiff, timePlus } from './internal/math';
import { TIME } from './internal/symbols';
import { NANOS_OF_DAY } from './internal/types';
import { TIME_EXTENDED, TIME_BASIC } from './internal/patterns';
import { D6, D3, D2 } from "./internal/formats";
import { Temporal, Components, TimeDelta, TimeUnit } from './types';
import { ZoneOffset } from './Zone';
import { Clock } from './Clock';
import { CivilDate } from './CivilDate';
import { CivilDateTime } from './CivilDateTime';
import { OffsetTime } from './OffsetTime';
import { Duration } from './Duration';
import { Equatable, Comparable, Equaler } from '@esfx/equatable';

const timeExtendedRegExp = new RegExp(R`^${TIME_EXTENDED}$`);
const timeBasicRegExp = new RegExp(R`^${TIME_BASIC}$`);

export class CivilTime implements Temporal<CivilTime>, Equatable, Comparable {
    static readonly START_OF_DAY = new CivilTime(0, 0);
    static readonly END_OF_DAY = new CivilTime(24, 0);

    /* @internal */ private [TIME]: NANOS_OF_DAY;

    constructor(hour: number, minute: number, second: number = 0, millisecond: number = 0, nanosecond: number = 0) {
        if (!isInt(hour) || hour < 0 || hour > 24) throw new RangeError(`The value ${hour} is not a valid hour.`);
        if (!isInt(minute) || minute < 0 || minute > 59) throw new RangeError(`The value ${minute} is not a valid minute.`);
        if (!isInt(second) || second < 0 || second > 59) throw new RangeError(`The value ${second} is not a valid second.`);
        if (!isInt(millisecond) || millisecond < 0 || millisecond > 1e3) throw new RangeError(`The value ${millisecond} is not a valid millisecond.`);
        if (!isInt(nanosecond) || nanosecond < 0 || nanosecond > 1e6) throw new RangeError(`The value ${nanosecond} is not a valid nanosecond.`);
        if (hour === 24 && minute !== 0) throw new RangeError(`The value ${minute} is not a valid minute of hour ${hour}.`);
        if (hour === 24 && second !== 0) throw new RangeError(`The value ${second} is not a valid second of hour ${hour}.`);
        if (hour === 24 && millisecond !== 0) throw new RangeError(`The value ${millisecond} is not a valid second of hour ${millisecond}.`);
        if (hour === 24 && nanosecond !== 0) throw new RangeError(`The value ${nanosecond} is not a valid second of hour ${nanosecond}.`);
        this[TIME] = getTime(hour, minute, second, millisecond, nanosecond);
    }

    get hour() { return this[TIME] === NANOSECONDS_PER_DAY ? 24 : getTimePart(this[TIME], TimePart.hour); }
    get minute() { return getTimePart(this[TIME], TimePart.minute); }
    get second() { return getTimePart(this[TIME], TimePart.second); }
    get millisecond() { return getTimePart(this[TIME], TimePart.millisecond); }
    get nanosecond() { return getTimePart(this[TIME], TimePart.nanosecond); }

    plus(deltas: Components<TimeDelta> = {}) {
        const { hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isInt(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an integer.`);
        if (!isInt(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an integer.`);
        if (!isInt(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an integer.`);
        if (!isInt(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an integer.`);
        if (!isInt(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an integer.`);
        return CivilTime.fromNanoseconds(timePlus(this[TIME], hours, minutes, seconds, milliseconds, nanoseconds));
    }

    minus(deltas: Components<TimeDelta> = {}) {
        const { hours = 0, minutes = 0, seconds = 0, milliseconds = 0, nanoseconds = 0 } = deltas;
        if (!isInt(hours)) throw new RangeError(`The value ${hours} is not valid for 'hours' as it is not an integer.`);
        if (!isInt(minutes)) throw new RangeError(`The value ${minutes} is not valid for 'minutes' as it is not an integer.`);
        if (!isInt(seconds)) throw new RangeError(`The value ${seconds} is not valid for 'seconds' as it is not an integer.`);
        if (!isInt(milliseconds)) throw new RangeError(`The value ${milliseconds} is not valid for 'milliseconds' as it is not an integer.`);
        if (!isInt(nanoseconds)) throw new RangeError(`The value ${nanoseconds} is not valid for 'nanoseconds' as it is not an integer.`);
        return CivilTime.fromNanoseconds(timePlus(this[TIME], -hours, -minutes, -seconds, -milliseconds, -nanoseconds));
    }

    with(components: Components<TimeUnit> = {}) {
        const {
            hour = this.hour,
            minute = this.minute,
            second = this.second,
            millisecond = this.millisecond,
            nanosecond = this.nanosecond
        } = components;
        return new CivilTime(hour, minute, second, millisecond, nanosecond);
    }

    withDate(date: CivilDate) {
        if (!(date instanceof CivilDate)) throw new TypeError("CivilDate expected: date");
        return CivilDateTime.from(date, this);
    }

    withOffset(offset: ZoneOffset) {
        if (!(offset instanceof ZoneOffset)) throw new TypeError("ZoneOffset expected: offset");
        return new OffsetTime(offset, this.hour, this.minute, this.second, this.millisecond, this.nanosecond);
    }

    until(other: CivilTime): Duration {
        if (!(other instanceof CivilTime)) throw new TypeError("CivilTime expected: other");
        return new Duration(timeDiff(this[TIME], other[TIME]));
    }

    since(other: CivilTime): Duration {
        if (!(other instanceof CivilTime)) throw new TypeError("CivilTime expected: other");
        return other.until(this);
    }

    toString() {
        return `${D2(this.hour)}:${D2(this.minute)}:${D2(this.second)}.${D3(this.millisecond)}${D6(this.nanosecond)}`;
    }

    toISOString() {
        return this.toString();
    }

    toJSON() {
        return this.toString();
    }

    equals(other: CivilTime) {
        return other !== null
            && other !== undefined
            && this[TIME] === other[TIME];
    }

    compareTo(other: CivilTime): number {
        return compareBigInts(this[TIME], other[TIME]);
    }

    /**
     * Creates a CivilTime from the provided time components.
     */
    static fromTime(hour: number, minute: number, second = 0, millisecond = 0, nanosecond = 0) {
        return new CivilTime(hour, minute, second, millisecond, nanosecond);
    }

    /**
     * Creates a CivilTime from the number of milliseconds since the start of the day.
     */
    static fromMilliseconds(milliseconds: number, nanosecond = 0) {
        if (!isInt(milliseconds) || milliseconds < 0) throw new RangeError(`The value ${milliseconds} is not valid for the number of milliseconds.`);
        if (!isInt(nanosecond) || nanosecond < 0 || nanosecond > 1e6) throw new RangeError(`The value ${nanosecond} is not a valid nanosecond.`);
        return CivilTime.fromNanoseconds(getTime(0, 0, 0, milliseconds, nanosecond));
    }

    /**
     * Creates a CivilTime from the number of nanoseconds since the start of the day.
     */
    static fromNanoseconds(nanoseconds: bigint): CivilTime {
        if (nanoseconds < 0 || nanoseconds > NANOSECONDS_PER_DAY) throw new RangeError(`The value ${nanoseconds} is not a valid number of nanoseconds`);
        return Object.create(CivilTime.prototype, { [TIME]: { value: nanoseconds } });
    }

    static fromString(text: string) {
        const normalText = normalizeString(text);
        const match = timeExtendedRegExp.exec(normalText) || timeBasicRegExp.exec(normalText);
        if (match) {
            const { hour, minute, second, millisecond = "", nanosecond = "" } = match.groups!;
            return new CivilTime(+hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
        }

        throw new Error(`Invalid time string '${text}'.`);
    }

    static now(clock: Clock) {
        return clock.currentInstant().withZone(clock.zone).toCivilTime();
    }



    [Equatable.equals](other: unknown) {
        return other instanceof CivilTime
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = CIVILTIME_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[TIME]);
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof CivilTime)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(CivilTime.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "CivilTime"
});